import React, { FC, useState, useEffect, useRef } from "react";
import { 
  Card, 
  Button, 
  Space, 
  Form, 
  Steps,
  Typography,
  notification,
  Spin,
  message
} from "antd";
import dayjs from 'dayjs';
import { 
  FileTextOutlined, 
  HomeOutlined,
  SaveOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthToken } from '../utils/functions';
import { CaseNumbersUrl, CaseDetailsUrl, BaseUrl } from '../utils/network';
import axios from 'axios';
import { getCaseSections, CaseSectionConfig } from '../utils/caseSections';

// Import all section components
import GeneralInformation from '../components/caseSections/GeneralInformation';
import Handicap from '../components/caseSections/Handicap';
import Accommodation from '../components/caseSections/Accommodation';
import Age from '../components/caseSections/Age';
import Issues from '../components/caseSections/Issues';
import Losses from '../components/caseSections/Losses';
import Harassment from '../components/caseSections/Harassment';
import PositionInformation from '../components/caseSections/PositionInformation';
import SelecteesInformation from '../components/caseSections/SelecteesInformation';
import SexualHarassment from '../components/caseSections/SexualHarassment';
import Termination from '../components/caseSections/Termination';
import CoworkersInformation from '../components/caseSections/CoworkersInformation';
import Responses from '../components/caseSections/Responses';
import AnalysisResultsModal from '../components/AnalysisResultsModal';

const { Title } = Typography;
const { Step } = Steps;

interface StepConfig {
  key: string;
  title: string;
  component: React.ReactNode;
  required: boolean;
}

const CaseCreationWizard: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [caseType, setCaseType] = useState<string | undefined>();
  const [discriminationBasis, setDiscriminationBasis] = useState<string | undefined>();
  const [sections, setSections] = useState<CaseSectionConfig>(getCaseSections(undefined, undefined));
  const [nextCaseNo, setNextCaseNo] = useState<number | null>(null);
  const [fetchingCaseNo, setFetchingCaseNo] = useState(true);
  const [createdCaseNumberId, setCreatedCaseNumberId] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Analysis results modal state
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [caseResponse, setCaseResponse] = useState<any>(null);
  
  // Viewing mode (for existing cases)
  const caseId = searchParams.get('caseId');
  const isViewMode = !!caseId;
  const [loadingCase, setLoadingCase] = useState(false);

  // Update sections when case type or basis changes
  useEffect(() => {
    const newSections = getCaseSections(caseType, discriminationBasis);
    setSections(newSections);
  }, [caseType, discriminationBasis]);

  // Load existing case or fetch next case number
  useEffect(() => {
    const loadData = async () => {
      const headers = getAuthToken();
      if (!headers) {
        return;
      }

      if (isViewMode && caseId) {
        // Load existing case
        try {
          setLoadingCase(true);
          const response = await axios.get(`${CaseDetailsUrl}${caseId}/`, headers);
          const caseData = response.data;
          
          // Convert date strings to dayjs objects for DatePicker
          if (caseData.date_discrimination_occurred) {
            caseData.date_discrimination_occurred = dayjs(caseData.date_discrimination_occurred);
          }
          if (caseData.date_of_interview) {
            caseData.date_of_interview = dayjs(caseData.date_of_interview);
          }
          
          // Set form values
          form.setFieldsValue(caseData);
          setCaseType(caseData.case_type);
          setDiscriminationBasis(caseData.discrimination_basis);
          setNextCaseNo(caseData.case_no || caseData.case_number_detail?.case_no);
          
          // Store case response for PDF access
          setCaseResponse(caseData);
          
          setFetchingCaseNo(false);
          setLoadingCase(false);
        } catch (error) {
          console.error('Error loading case:', error);
          notification.error({
            message: 'Error',
            description: 'Failed to load case data. Please try again.',
          });
          setLoadingCase(false);
          setFetchingCaseNo(false);
        }
      } else {
        // Fetch next case number for new case
        try {
          const response = await axios.get(`${CaseNumbersUrl}next/`, headers);
          const nextNo = response.data.next_case_no;
          setNextCaseNo(nextNo);
          form.setFieldsValue({ case_no: nextNo });
        } catch (error) {
          console.error('Error fetching next case number:', error);
          notification.error({
            message: 'Error',
            description: 'Failed to fetch next case number. Please refresh the page.',
          });
        } finally {
          setFetchingCaseNo(false);
        }
      }
    };

    loadData();
  }, [form, isViewMode, caseId]);

  // Cleanup: Delete case number if component unmounts without successful submission
  const createdCaseNumberIdRef = useRef<number | null>(null);
  const isSubmittedRef = useRef<boolean>(false);
  
  useEffect(() => {
    createdCaseNumberIdRef.current = createdCaseNumberId;
  }, [createdCaseNumberId]);
  
  useEffect(() => {
    isSubmittedRef.current = isSubmitted;
  }, [isSubmitted]);
  
  useEffect(() => {
    return () => {
      // Cleanup on unmount only - delete case number if it was created but not submitted
      if (createdCaseNumberIdRef.current && !isSubmittedRef.current) {
        const cleanup = async () => {
          try {
            const headers = getAuthToken();
            if (headers) {
              await axios.delete(`${CaseNumbersUrl}${createdCaseNumberIdRef.current}/`, headers);
              console.log(`Cleaned up case number ${createdCaseNumberIdRef.current}`);
            }
          } catch (error) {
            console.error('Error cleaning up case number:', error);
          }
        };
        cleanup();
      }
    };
  }, []);

  // Watch form values for case type and discrimination basis
  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.case_type !== undefined) {
      setCaseType(changedValues.case_type);
    }
    if (changedValues.discrimination_basis !== undefined) {
      setDiscriminationBasis(changedValues.discrimination_basis);
    }
  };

  // Build steps array based on visible sections
  const buildSteps = (): StepConfig[] => {
    const steps: StepConfig[] = [];

    // Step 1: General Information (always first)
    if (sections.showGeneralInformation) {
      steps.push({
        key: 'general',
        title: 'General Information',
        component: (
          <GeneralInformation 
            form={form} 
            discriminationBasis={discriminationBasis} 
            caseNo={nextCaseNo}
            loadingCaseNo={fetchingCaseNo}
          />
        ),
        required: true
      });
    }

    // Step 2: Handicap
    if (sections.showHandicap) {
      steps.push({
        key: 'handicap',
        title: 'Handicap Information',
        component: <Handicap form={form} />,
        required: false
      });
    }

    // Step 3: Accommodation
    if (sections.showAccommodation) {
      steps.push({
        key: 'accommodation',
        title: 'Accommodation',
        component: <Accommodation form={form} />,
        required: false
      });
    }

    // Step 4: Age
    if (sections.showAge) {
      steps.push({
        key: 'age',
        title: 'Age Information',
        component: <Age form={form} />,
        required: false
      });
    }

    // Step 5: Issues
    if (sections.showIssues) {
      steps.push({
        key: 'issues',
        title: 'Issues',
        component: <Issues form={form} />,
        required: false
      });
    }

    // Step 6: Losses
    if (sections.showLosses) {
      steps.push({
        key: 'losses',
        title: 'Losses',
        component: <Losses form={form} />,
        required: false
      });
    }

    // Step 7: Harassment
    if (sections.showHarassment) {
      steps.push({
        key: 'harassment',
        title: 'Harassment',
        component: <Harassment form={form} />,
        required: false
      });
    }

    // Step 8: Position Information
    if (sections.showPositionInformation) {
      steps.push({
        key: 'position',
        title: 'Position Information',
        component: <PositionInformation form={form} />,
        required: false
      });
    }

    // Step 9: Selectees Information
    if (sections.showSelecteesInformation) {
      steps.push({
        key: 'selectees',
        title: 'Selectees Information',
        component: <SelecteesInformation form={form} />,
        required: false
      });
    }

    // Step 10: Sexual Harassment
    if (sections.showSexualHarassment) {
      steps.push({
        key: 'sexual_harassment',
        title: 'Sexual Harassment',
        component: <SexualHarassment form={form} />,
        required: false
      });
    }

    // Step 11: Termination
    if (sections.showTermination) {
      steps.push({
        key: 'termination',
        title: 'Termination',
        component: <Termination form={form} />,
        required: false
      });
    }

    // Step 12: Coworkers Information
    if (sections.showCoworkersInformation) {
      steps.push({
        key: 'coworkers',
        title: 'Coworkers Information',
        component: <CoworkersInformation form={form} />,
        required: false
      });
    }

    // Step 13: Responses
    if (sections.showResponses) {
      steps.push({
        key: 'responses',
        title: 'Responses',
        component: <Responses form={form} />,
        required: false
      });
    }

    return steps;
  };

  const steps = buildSteps();
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Handle next step
  const handleNext = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      // Validate current step fields
      const currentStepConfig = steps[currentStep];
      const fieldNames = getFieldNamesForStep(currentStepConfig.key);
      
      if (currentStepConfig.required && fieldNames.length > 0) {
        await form.validateFields(fieldNames);
      }

      if (!isLastStep) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Get field names for a step (simplified - you may need to refine this)
  const getFieldNamesForStep = (stepKey: string): string[] => {
    const fieldMap: Record<string, string[]> = {
      general: ['case_type', 'discrimination_basis', 'first_name', 'last_name'],
      handicap: ['qualified_for_job', 'handicap_prevents_job_performance'],
      accommodation: ['accommodation_requested'],
      age: ['your_age', 'accused_age'],
      issues: ['category'],
      losses: ['loss_type'],
      harassment: ['harassment_type'],
      position: ['position_title'],
      selectees: ['selectee_name'],
      sexual_harassment: ['harassment_type'],
      termination: ['termination_date'],
      coworkers: ['coworker_name'],
      responses: ['response_type']
    };
    return fieldMap[stepKey] || [];
  };

  // Handle cancel
  const handleCancel = async () => {
    if (createdCaseNumberId && !isSubmitted) {
      try {
        const headers = getAuthToken();
        if (headers) {
          await axios.delete(`${CaseNumbersUrl}${createdCaseNumberId}/`, headers);
          console.log(`Cleaned up case number ${createdCaseNumberId} due to cancellation`);
        }
      } catch (error) {
        console.error('Error cleaning up case number:', error);
      }
    }
    navigate('/');
  };

  // Handle form submission
  const onSubmit = async (values: any) => {
    setLoading(true);

    try {
      const headers = getAuthToken();
      if (!headers) {
        throw new Error('Authentication required');
      }

      // Step 1: Create case number (auto-generated by backend)
      let caseNumberId;
      let generatedCaseNo: number;
      
      try {
        const createCaseNumberResponse = await axios.post(
          CaseNumbersUrl,
          {},
          headers
        );
        caseNumberId = createCaseNumberResponse.data.id;
        generatedCaseNo = createCaseNumberResponse.data.case_no;
        
        setCreatedCaseNumberId(caseNumberId);
        form.setFieldsValue({ case_no: generatedCaseNo });
        
        const nextResponse = await axios.get(`${CaseNumbersUrl}next/`, headers);
        setNextCaseNo(nextResponse.data.next_case_no);
      } catch (error: any) {
        throw new Error(`Failed to create case number: ${error.response?.data?.detail || error.message}`);
      }

      // Step 2: Prepare case details data
      const caseDetailsData: any = {
        case_number: caseNumberId,
        case_type: values.case_type,
        discrimination_basis: values.discrimination_basis,
        // Convert dayjs objects to ISO date strings
        date_discrimination_occurred: values.date_discrimination_occurred 
          ? (typeof values.date_discrimination_occurred === 'string' 
              ? values.date_discrimination_occurred 
              : values.date_discrimination_occurred.format('YYYY-MM-DD'))
          : null,
        date_of_interview: values.date_of_interview 
          ? (typeof values.date_of_interview === 'string' 
              ? values.date_of_interview 
              : values.date_of_interview.format('YYYY-MM-DD'))
          : null,
        last_name: values.last_name,
        first_name: values.first_name,
        middle: values.middle,
        company_name: values.company_name,
        accused_last_name: values.accused_last_name,
        accused_first_name: values.accused_first_name,
      };

      // Add basis-specific fields
      if (values.your_age) caseDetailsData.your_age = values.your_age;
      if (values.your_color) caseDetailsData.your_color = values.your_color;
      if (values.your_handicap) caseDetailsData.your_handicap = values.your_handicap;
      if (values.your_national_origin) caseDetailsData.your_national_origin = values.your_national_origin;
      if (values.your_race) caseDetailsData.your_race = values.your_race;
      if (values.your_religion) caseDetailsData.your_religion = values.your_religion;
      if (values.your_reprisal) caseDetailsData.your_reprisal = values.your_reprisal;
      if (values.your_sex) caseDetailsData.your_sex = values.your_sex;

      if (values.accused_age) caseDetailsData.accused_age = values.accused_age;
      if (values.accused_color) caseDetailsData.accused_color = values.accused_color;
      if (values.accused_handicap) caseDetailsData.accused_handicap = values.accused_handicap;
      if (values.accused_national_origin) caseDetailsData.accused_national_origin = values.accused_national_origin;
      if (values.accused_race) caseDetailsData.accused_race = values.accused_race;
      if (values.accused_religion) caseDetailsData.accused_religion = values.accused_religion;
      if (values.accused_reprisal) caseDetailsData.accused_reprisal = values.accused_reprisal;
      if (values.accused_sex) caseDetailsData.accused_sex = values.accused_sex;

      // Add nested model data
      if (sections.showHandicap && values.handicap_info) {
        caseDetailsData.handicap_info = values.handicap_info;
      }
      if (sections.showAccommodation && values.accommodation_info) {
        caseDetailsData.accommodation_info = values.accommodation_info;
      }
      if (sections.showAge && values.age_info) {
        caseDetailsData.age_info = values.age_info;
      }
      if (sections.showIssues && values.issues_info) {
        caseDetailsData.issues_info = values.issues_info;
      }
      if (sections.showLosses && values.losses_info) {
        caseDetailsData.losses_info = values.losses_info;
      }
      if (sections.showHarassment && values.harassment_info) {
        caseDetailsData.harassment_info = values.harassment_info;
      }
      if (sections.showPositionInformation && values.position_info) {
        caseDetailsData.position_info = values.position_info;
      }
      if (sections.showSelecteesInformation && values.selectees_info) {
        caseDetailsData.selectees_info = values.selectees_info;
      }
      if (sections.showSexualHarassment && values.sexual_harassment_info) {
        caseDetailsData.sexual_harassment_info = values.sexual_harassment_info;
      }
      if (sections.showTermination && values.termination_info) {
        caseDetailsData.termination_info = values.termination_info;
      }
      if (sections.showCoworkersInformation && values.coworkers_info) {
        caseDetailsData.coworkers_info = values.coworkers_info;
      }
      if (sections.showResponses && values.responses_info) {
        caseDetailsData.responses_info = values.responses_info;
      }

      // Step 3: Create case details
      const response = await axios.post(CaseDetailsUrl, caseDetailsData, headers);
      setCaseResponse(response.data);
      setIsSubmitted(true);
      setCreatedCaseNumberId(null);

      notification.success({
        message: 'Case Created Successfully',
        description: `Case #${generatedCaseNo} has been created and analyzed.`,
      });

      setShowResultsModal(true);
    } catch (error: any) {
      console.error('Error creating case:', error);
      
      let errorMessage = 'Failed to create case';
      let errorDescription = 'An unexpected error occurred. Please try again.';

      if (error.response) {
        errorDescription = error.response.data?.detail || error.response.data?.message || errorDescription;
        if (error.response.status === 401) {
          errorMessage = 'Authentication Error';
          errorDescription = 'Your session has expired. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Permission Denied';
          errorDescription = 'You do not have permission to perform this action.';
        } else if (error.response.status === 400) {
          errorMessage = 'Validation Error';
          if (error.response.data) {
            const errors = Object.entries(error.response.data)
              .map(([key, value]: [string, any]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('\n');
            errorDescription = errors;
          }
        }
      } else if (error.request) {
        errorDescription = 'No response from server. Please check your connection.';
      } else {
        errorDescription = error.message || errorDescription;
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
        duration: 6,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin 
      spinning={loading || loadingCase} 
      tip={loadingCase ? "Loading case..." : "Creating case..."} 
      size="large"
      indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
    >
      <div>
        {/* Header Card */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                <span style={{ fontSize: '24px', fontWeight: 600, color: '#000' }}>
                  {fetchingCaseNo ? 'Loading...' : `Case Number - ${nextCaseNo || 'N/A'}`}
                </span>
              </div>
              <Button 
                icon={<HomeOutlined />} 
                onClick={handleCancel}
                type="default"
              >
                Home
              </Button>
            </div>
            
            {/* Steps Indicator */}
            <Steps current={currentStep} size="small">
              {steps.map((step, index) => (
                <Step key={step.key} title={step.title} />
              ))}
            </Steps>
          </div>
        </Card>

        {/* Case Creation Form */}
        <Form 
          layout="vertical" 
          onFinish={(values) => {
            // Only submit if we're on the last step
            if (isLastStep) {
              onSubmit(values);
            }
          }}
          form={form}
          onValuesChange={handleFormValuesChange}
          scrollToFirstError
          onKeyDown={(e) => {
            // Prevent form submission on Enter key unless it's the last step and Finish button is focused
            if (e.key === 'Enter') {
              const target = e.target as HTMLElement;
              // Only allow Enter to submit if it's the Finish button or a textarea
              if (!isLastStep || (target.tagName !== 'BUTTON' && target.tagName !== 'TEXTAREA')) {
                e.preventDefault();
              }
            }
          }}
        >
          {/* Current Step Content */}
          <Card style={{ marginBottom: 16, minHeight: '400px' }}>
            {steps[currentStep]?.component}
          </Card>

          {/* Navigation Buttons */}
          <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button 
                onClick={handleCancel}
              >
                Cancel
              </Button>
              
              <Space>
                {!isFirstStep && (
                  <Button 
                    icon={<LeftOutlined />}
                    onClick={handlePrevious}
                  >
                    Back
                  </Button>
                )}
                
                {!isLastStep ? (
                  <Button 
                    type="primary"
                    icon={<RightOutlined />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNext(e);
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <>
                    {isViewMode ? (
                      <>
                        <Button 
                          icon={<DownloadOutlined />}
                          onClick={async () => {
                            if (caseResponse?.pdf_url) {
                              try {
                                // Construct full URL
                                const baseUrl = BaseUrl.replace('/api/', '');
                                const pdfUrl = caseResponse.pdf_url.startsWith('http') 
                                  ? caseResponse.pdf_url 
                                  : `${baseUrl}${caseResponse.pdf_url}`;
                                
                                // Fetch and download
                                const response = await fetch(pdfUrl, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}` || '',
                                  },
                                });

                                if (!response.ok) {
                                  throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                const blob = await response.blob();
                                const downloadUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = caseResponse.pdf_filename || `case_${nextCaseNo}_report.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(downloadUrl);
                                
                                message.success('Report downloaded successfully');
                              } catch (error) {
                                console.error('Error downloading PDF:', error);
                                message.error('Failed to download report');
                              }
                            } else {
                              message.error('PDF report not available');
                            }
                          }}
                        >
                          Download Report
                        </Button>
                        <Button 
                          type="primary"
                          onClick={() => navigate('/cases')}
                        >
                          Finish
                        </Button>
                      </>
                    ) : (
                      <Button 
                        htmlType="button"
                        type="primary" 
                        loading={loading}
                        icon={<SaveOutlined />}
                        size="large"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Manually trigger form submission
                          try {
                            // Get ALL form values, not just validated ones
                            const allValues = form.getFieldsValue(true);
                            console.log('[DEBUG] All form values on submit:', allValues);
                            await onSubmit(allValues);
                          } catch (error) {
                            console.error('Form submission error:', error);
                          }
                        }}
                      >
                        Finish
                      </Button>
                    )}
                  </>
                )}
              </Space>
            </Space>
          </Card>
        </Form>
      </div>
      
      {/* Analysis Results Modal */}
      {caseResponse && (
        <AnalysisResultsModal
          visible={showResultsModal}
          onClose={() => {
            setShowResultsModal(false);
            form.resetFields();
            setCaseType(undefined);
            setDiscriminationBasis(undefined);
            setSections(getCaseSections(undefined, undefined));
            setCreatedCaseNumberId(null);
            setCaseResponse(null);
            setCurrentStep(0);
            form.setFieldsValue({ case_no: nextCaseNo });
            navigate('/');
          }}
          analysisResults={{
            likelihood: (caseResponse.likelihood || 'Medium') as 'High' | 'Moderate' | 'Low',
            confidence_score: caseResponse.weighted_score ? parseFloat(caseResponse.weighted_score) / 10 : 0.5,
            case_ref: `Case #${caseResponse.case_no || caseResponse.case_number_detail?.case_no || 'N/A'}`,
            reasoning: `Based on the analysis, this case has a ${caseResponse.likelihood || 'Medium'} likelihood index with a weighted score of ${caseResponse.weighted_score || 'N/A'}.`
          }}
          caseNumber={caseResponse.case_no || caseResponse.case_number_detail?.case_no || 0}
          pdfUrl={caseResponse.pdf_url}
          pdfFilename={caseResponse.pdf_filename}
        />
      )}
    </Spin>
  );
};

export default CaseCreationWizard;

